import { jwtTokenService } from '@mathflat/shared/@modules/auth/jwtTokenService'
import { bindI18nBrand } from '@mathflat/shared/i18n/i18n'
import { readBridgeToken } from '@mathflat/vine-auth/bridge-token'
import { addWeeks } from 'date-fns'
import { runInAction } from 'mobx'
import qs from 'qs'
import { matchPath } from 'react-router'

import designTemplateService from './contentComponents/common/WorksheetDesign/DesignTemplate.service'
import { academyController } from './controller/academy.controller'
import { contractController } from './controller/contract.controller'
import { guestController } from './controller/guest.controller'
import { teacherController } from './controller/teacher.controller'
import { userController } from './controller/user.controller'
import { UserPermission } from './models/(Permission)/UserPermission'
import { updatedContentsService } from './page/etc/UpdatedContents/UpdatedContents.service'
import kmmRegisterService from './page/Lesson-prepare/PageKMM/register/_services/KMMRegister.service'
import { podMallCartService } from './page/PODMall/pages/PODMallCart/_service/PODMallCart.service'
import { podMallUserService } from './page/PODMall/pages/PODMallManage/PODMallUser/_service/PODMallUser.service'
import commonRepo from './repository/common.repo'
import {
  type AuthRoute,
  getEntrancePage,
  isPublicRoute,
  LOGIN_ENTRY_ROUTES,
  NOT_ALLOWED_TO_REDIRECT_TO_LOGIN_PATH,
  RouteName,
} from './router'
import { authService } from './service/auth.service'
import { curriculumCoreService } from './service/curriculumCore.service'
import { PaymentService } from './service/Payment.service'
import {
  clearStorageForSignOut,
  localStorageService,
  writeLocalItemSafely,
} from './service/storage.service'
import { isPhoneDevice } from './service/viewport.service'
import type { AppDeviceInfo } from './service/webview.service'
import WebviewService from './service/webview.service'
import { remoteStorageService } from './utils/axios/remoteStorage'
import { initAxiosDefaultBaseURL } from './utils/axios/util'
import dateUtils from './utils/DateUtils'
import { envUtils } from './utils/envUtils'
import { buildLoginRedirectSearchParams, isNonRedirectPath } from './utils/loginRedirect'
import { shouldReportBootFailure } from './utils/modules/errorReporting/bootFailure'
import { reporter } from './utils/modules/errorReporting/reporter'
import { writeTeacherRoleCookie } from './utils/roleCookie'

type CRMLoginQSType = {
  accessToken?: string
  refreshToken?: string
  userId?: string
  academyId?: string
}

export type AfterLoginRedirectPathType = {
  redirectPathAfterLogin?: string
}

type SearchType = Partial<AppDeviceInfo> &
  CRMLoginQSType &
  AfterLoginRedirectPathType & { promo?: string }

export const checkAcademyInfoUpdatePeriod = (
  targetDate: Date,
  eventStartDate: Date,
  eventEndDate: Date = addWeeks(eventStartDate, 4),
) => {
  // TODO: 학원정보 업데이트 이벤트 기간이 아직 정해지지 않아서 시작일을 우선 임의 값으로 설정
  // - 차후 기간 업데이트 필요
  // - 로컬스토리지 값으로 테스트 가능하도록 구현

  const isEventPeriod = dateUtils.checkIsWithinInterval(new Date(), {
    startDate: eventStartDate,
    endDate: eventEndDate,
  })

  return {
    isEventPeriod,
    isUpdatedWithInEventPeriod:
      isEventPeriod &&
      dateUtils.checkIsWithinInterval(targetDate, {
        startDate: eventStartDate,
        endDate: eventEndDate,
      }),
  }
}

const isNotAllowedToRedirectToLoginPath = (pathname: string) => {
  return NOT_ALLOWED_TO_REDIRECT_TO_LOGIN_PATH.some((route) =>
    matchPath(pathname, {
      path: route,
      // 목록에 root('/')가 포함되어 있어 exact 없이는 모든 경로가 매치되고,
      // 그 결과 redirectPathAfterLogin 이 어떤 경로에서도 붙지 않게 된다.
      exact: true,
    }),
  )
}

/** 다른 탭의 토큰 핸드오프 응답을 기다리는 시간. BroadcastChannel 은 같은 오리진 탭 간 즉시 전달된다. */
const TOKEN_HANDOFF_TIMEOUT_MS = 500

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * 로그인 페이지로 리다이렉트
 * 특수 로그인 페이지(부산, 전남, 공공) 경로를 유지하고, 현재 경로를 redirectPathAfterLogin으로 설정
 */
const redirectToLoginPath = () => {
  const { pathname } = window.location

  if (isNonRedirectPath(pathname)) {
    return
  }

  const loginPath = localStorageService.getItem('LOGIN_PATH') || '/login'

  // 현재 search 는 redirectPathAfterLogin 안에 통째로 보존되므로
  // 로그인 URL 에 중복으로 싣지 않는다 (웹뷰 식별 파라미터 version/device 만 예외적으로 최상위 보존)
  const searchParams = isNotAllowedToRedirectToLoginPath(pathname)
    ? new URLSearchParams(window.location.search)
    : buildLoginRedirectSearchParams(pathname, window.location.search)

  commonRepo.router.replace(`${loginPath}?${searchParams.toString()}`)
}

/** 로그인 뒤 첫 화면으로 간다. 보지 않은 콘텐츠 업데이트가 있으면 그 안내가 먼저다. */
export const goToEntrancePage = () => {
  const updatedContent = updatedContentsService.getUpdatedContent()
  if (updatedContent) {
    updatedContentsService.updateStoredInfo(updatedContent.id)
  }
  commonRepo.router.push(
    getEntrancePage(
      { isSchoolflat: commonRepo.isSchoolflat, isSeoltab: commonRepo.isSeoltab },
      !!updatedContent,
    ),
  )
}

const authDaemon = async () => {
  const { search, pathname } = window.location

  const {
    accessToken,
    refreshToken,
    userId,
    academyId,
    redirectPathAfterLogin,
    promo,
    ...webviewParams
  } = qs.parse(search, {
    ignoreQueryPrefix: true,
  }) as unknown as SearchType

  // 셸이 넘기는 promo 는 진입 URL 쿼리에만 실려 오고 라우팅 과정에서 사라질 수 있으므로,
  // 원본 URL 을 볼 수 있는 부팅 시점에 억제 여부를 확정해 둔다.
  if (promo === 'false') {
    writeLocalItemSafely('DISABLE_LOGIN_PAGE_PROMOTION', true)
  }

  // CRM에서 로그인한 케이스
  if (accessToken && refreshToken && userId && academyId) {
    jwtTokenService.clearTokens()
    jwtTokenService.setTokens({ accessToken, refreshToken })
    authService.saveLoginResponseToStorage({
      userId,
      academyId,
    })

    const url = new URL(window.location.href)
    url.searchParams.delete('accessToken')
    url.searchParams.delete('refreshToken')
    url.searchParams.delete('userId')
    url.searchParams.delete('academyId')
    history.replaceState(null, '', url)
  }

  // 이 아래 인증 초기화가 실패하면 early return 하므로, 셸이 준 값은 그전에 확보해 둔다
  if (WebviewService.isAppAndroid) {
    WebviewService.setAppDeviceInfo(webviewParams)
  }

  jwtTokenService.setEventHandlers({
    onReissueSuccess: ({ restrictions }) => {
      commonRepo.permission?.updateRestrictions(restrictions ?? [])
    },
  })

  // JWT 인증 초기화 (레거시 토큰 마이그레이션 포함)
  try {
    await authService.initializeJwtAuth()
  } catch (error) {
    // 마이그레이션 실패 시 스토리지 클리어 후 로그인 페이지로 리다이렉트
    jwtTokenService.clearTokens()
    clearStorageForSignOut()
    redirectToLoginPath()
    return
  }

  // TODO: axios.default.baseURL 세팅이 화면단에서 이뤄져서 authDaemon보다 늦어져서
  // authDaemon 내 api가 세팅되었어야할 baseURL과 달라짐.
  // 일단 화면단의 로직을 공통함수로 빼서 authDaemon 상단에서 먼저 실행하는 식으로 고쳤지만,
  // 더 좋은 방법이 있으면 수정할 것.
  if (envUtils.isDev) {
    initAxiosDefaultBaseURL(localStorageService.getItem('DEV_API') ?? 'dev')
  }

  if (isPublicRoute(pathname)) return

  // 토큰을 들고 브릿지로 들어왔으면 그 계정으로 들어가겠다는 뜻이다. 여기서 기존 세션을 이어
  // 진입 네비게이션까지 해버리면 브릿지가 로그인하기도 전에 화면이 떠나 새 계정이 묻힌다.
  if (pathname === RouteName.vineBridge && readBridgeToken(window.location.search)) return

  // 임시 - 차후 localstorageService로 교체
  const teacherId = localStorage.getItem('teacher-id')
  if (teacherId) {
    localStorageService.setItem('TEACHER_ID')(teacherId)
  }

  // 로그인 되어 있다면
  if (jwtTokenService.isLoggedIn && teacherId) {
    remoteStorageService.initRelationId(teacherId)
    // await 하지 않는 조회라 실패가 아래 try/catch 로 잡히지 않는다.
    // allSettled 로 묶어 처리되지 않은 프로미스 거부로 남지 않게 한다.
    void Promise.allSettled([
      remoteStorageService.get('visited유형분석'),
      remoteStorageService.get('visitedWorksheetBoardView'),
      remoteStorageService.get('visitedWorkbookBoardView'),
      remoteStorageService.get('learningStatisticsSortedType'),
      remoteStorageService.get('ONE_CLICK_WORKSHEET_OPTIONS'),
      remoteStorageService.get('ESSAY_WORKSHEET_OPEN_NOTIFICATION'),
      remoteStorageService.get('PUBLIC_WORKBOOK_OPEN_NOTIFICATION'),
    ]).then((results) => {
      const rejected = results.filter((result) => result.status === 'rejected')
      if (rejected.length) {
        console.warn('원격 저장소 조회 실패', rejected.length, '건', rejected[0])
      }
    })
    await remoteStorageService.get('PREVIOUS_SELECTED_CURRICULUM_INFO_유형분석')
    if (remoteStorageService.PREVIOUS_SELECTED_CURRICULUM_INFO_유형분석) {
      await remoteStorageService.migrateAchievementAnalysisCurriculumInfoData()
    }

    try {
      // await 하지 않는 비핵심 부팅 리소스라 실패가 바깥 try/catch 로 잡히지 않는다.
      // 각자 격리해 처리되지 않은 프로미스 거부로 남지 않게 한다.
      designTemplateService.fetchAllDesignTemplateList().catch((error) => {
        console.warn('학습지 디자인 템플릿 목록 조회 실패', error)
      })
      podMallCartService.fetchSetCartItems().catch((error) => {
        console.warn('POD몰 장바구니 조회 실패', error)
      })
      podMallUserService.fetchSetUser().catch((error) => {
        console.warn('POD몰 사용자 조회 실패', error)
      })
      const [user, academy, b2gContractInfo, me] = await Promise.all([
        teacherController.fetchUserTeacher(teacherId),
        academyController.fetchMyAcademy(),
        contractController.fetchContractInfo(),
        userController.fetchMe(),
        kmmRegisterService.initialize(),
      ])

      localStorageService.setItem('ACADEMY_ID')(user.academyId)
      writeTeacherRoleCookie()

      runInAction(() => {
        commonRepo.user = user
        commonRepo.academy = academy
        commonRepo.payment = new PaymentService()
        commonRepo.permission = new UserPermission({
          userAuthority: user.type,
          academyFeatureAuthority: academy.authority,
          subscribedCurriculums: academy.subscribeItems,
          payStatus: academy.payStatus,
          isSchoolflat:
            envUtils.isSchool || !!(academy.customerType && academy.customerType.includes('B2G')),
          isSeoltab: academy.academyAdditionalData.isSeoltab,
          isFranchise: academy.isFranchise,
          restrictions: me.restrictions ?? [],
        })
        commonRepo.b2gContract = b2gContractInfo
      })

      // prefetchQuery 는 실패를 내부에서 삼킨다(로그인 흐름과 무관) — 실제 사용 시 fetchGuests 가 재시도한다.
      if (commonRepo.permission.byFeature.isAllowed('CREVERSE_GUEST')) {
        void guestController.prefetchGuests()
      }

      await curriculumCoreService.fetchSetCurriculum({
        plan: commonRepo.isSolo ? 'solo' : 'default',
        subscribedCurriculums: commonRepo.permission.bySubscription.솔로_구독_커리큘럼,
      })

      // 로그인 페이지로 첫 진입한 경우 부팅 시점엔 academy 가 없어 default 번들만 깔려 있다.
      // 로그인 완료 후 받아온 academy 의 brand 번들을 주입하고 remote 언어팩을 시도한다.
      // router.push 직전에 await 해, 다음 페이지가 default 로 mount 되어 brand 문구가 늦게 반영되는 race 를 막는다.
      // 비핵심 리소스라 실패가 바깥 catch(토큰 클리어/로그아웃)로 전파되지 않게 격리한다.
      try {
        await bindI18nBrand(
          { academyGroupType: academy.academyGroupType, customerType: academy.customerType },
          academy.languagePackFileUrl,
        )
      } catch (error) {
        console.warn('bindI18nBrand 실패, default 번들로 진행합니다.', error)
      }

      const loginPath = localStorageService.getItem('LOGIN_PATH') || '/login'
      if (!commonRepo.academy?.initialized) {
        // 로그인시, 초기 셋팅이 안된 유저는 이닛셋팅 페이지로 이동
        commonRepo.router.push(RouteName.initSetting)
      } else if (
        pathname === loginPath ||
        pathname === '/' ||
        LOGIN_ENTRY_ROUTES.includes(pathname as AuthRoute)
      ) {
        // 우선순위 1) redirectPathAfterLogin 있을 경우
        if (redirectPathAfterLogin) {
          const redirectParams = new URL(redirectPathAfterLogin, window.location.origin)
            .searchParams
          const expectedLoginId = redirectParams.get('expectedLoginId')

          if (!expectedLoginId) {
            commonRepo.router.push(redirectPathAfterLogin)
            return
          }

          // expectedLoginId가 있으면 현재 로그인 계정과 일치할 때만 동의 페이지로 이동
          const savedLoginId = String(localStorageService.getItem('SAVED_LOGIN_ID') ?? '')
          if (savedLoginId && savedLoginId === expectedLoginId) {
            commonRepo.router.push(redirectPathAfterLogin)
            return
          }
          // 계정 불일치 시 redirectPathAfterLogin 유지한 채 재로그인 유도
          jwtTokenService.clearTokens(false)
          window.location.replace(
            `${loginPath}?redirectPathAfterLogin=${encodeURIComponent(redirectPathAfterLogin)}`,
          )
          return
        }

        // 휴대폰은 채점과 데스크탑 버전 중 고르는 화면부터 연다. 콘텐츠 업데이트 안내는 데스크탑 버전을 고르면 본다.
        // 크레버스는 휴대폰 화면을 쓰지 않고, 수업 권한이 없으면 고를 채점이 없다.
        if (isPhoneDevice && !commonRepo.isCreverse && commonRepo.permission.get.lesson.access) {
          // 휴대폰 화면은 새 문서로 열린다. 쌓으면 뒤로 갔을 때 이 자리로 돌아와 다시 /mobile 로 밀려난다
          commonRepo.router.replace(RouteName.mobileHome)
          return
        }

        goToEntrancePage()
      }
    } catch (e) {
      // console 에 찍으면 RUM 이 에러로 수집해 토큰 만료 같은 정상 흐름이 노이즈가 된다.
      // 서버 요청 실패는 보고하지 않고(예상된 4xx·인터셉터가 이미 보고한 network·BE 몫인 5xx),
      // 지금까지 아무 데도 남지 않던 부팅 코드의 런타임 예외만 직접 보고한다.
      if (shouldReportBootFailure(e)) {
        reporter.capture(e, { mechanism: 'boot' })
      }
      //200이 아닐때 (토큰이 invalid하거나 기타 오류가 날 때)
      jwtTokenService.clearTokens()

      // CRM 로그인 플로우에서 토큰이 없다면 로그인 페이지로 이동
      if (accessToken || refreshToken || academyId || userId) {
        const loginPath = localStorageService.getItem('LOGIN_PATH') || '/login'
        window.location.replace(loginPath)
        return
      }

      redirectToLoginPath()
    }
  } else if (!jwtTokenService.isLoggedIn) {
    // 토큰이 없다 — 다른 탭이 갖고 있는지 물어본다.
    //
    // 응답 핸들러를 등록하지 않으면 응답이 와도 아무 일도 일어나지 않는다. 토큰을 받아도 authDaemon
    // 은 이미 지나갔으므로 리로드로 부트를 다시 태워야 하고, 다른 탭도 없다고 하면 로그인으로 보내야
    // 한다. 둘 다 없으면 미인증 상태로 화면이 그대로 렌더돼, 로그인 컨텍스트를 전제한 화면이 빈
    // 데이터로 터진다.
    jwtTokenService.setEventHandlers({
      onTokenReceived: () => window.location.reload(),
      onTokenNotFound: () => redirectToLoginPath(),
    })
    jwtTokenService.requestToken()

    // 응답해줄 탭이 아예 없으면 어떤 핸들러도 호출되지 않는다. 그 경우의 폴백.
    // 리다이렉트 대상이 아닌 경로(로그인·공개 라우트)에서는 기다릴 이유가 없어 즉시 통과시킨다.
    if (!isNonRedirectPath(pathname)) {
      await wait(TOKEN_HANDOFF_TIMEOUT_MS)
      if (!jwtTokenService.isLoggedIn) redirectToLoginPath()
    }
  } else {
    // 토큰은 있지만 teacherId가 없는 경우 (localStorage 부분 유실)
    // requestToken()을 호출하면 다른 탭이 토큰으로 응답 → reload → teacherId 없는 동일 상태 → 무한 반복
    // 토큰을 클리어하고 로그인 페이지로 이동
    jwtTokenService.clearTokens()
    redirectToLoginPath()
  }
}

export default authDaemon
